import React, { useState, useMemo, useEffect, useCallback } from "react";
import api from "api";

const MONTHS = [
  "Janvier", "Février", "Mars", "Avril", "Mai", "Juin", "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"
];

export default function StatsPopup({ open, onClose }) {
  const [copied, setCopied] = useState(false);
  const currentYear = new Date().getFullYear();
  // On veut démarrer sur l'année en cours à chaque ouverture
  const [year, setYear] = useState(currentYear);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open) setYear(currentYear);
  }, [open, currentYear]);

  // charge les statistiques de l'année sélectionnée
  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setError(null);
    const start = new Date(year, 0, 1).getTime();
    const end = new Date(year + 1, 0, 1).getTime();
    api.get(`quotes/stats/monthly/${start}?end=${end}`)
      .then(res => {
        setData(res.data);
        setLoading(false);
      })
      .catch(() => {
        setError("Erreur lors du chargement des statistiques");
        setLoading(false);
      });
  }, [open, year]);

  // Données pour l'année sélectionnée, avec 0 pour les mois sans devis
  const rowsForYear = useMemo(() => {
    return Array.from({ length: 12 }).map((_, i) => {
      const moisStr = `${year}-${(i + 1).toString().padStart(2, '0')}`;
      const found = data.find(r => r.mois === moisStr);
      return {
        mois: moisStr,
        location_confirmee: found?.location_confirmee ?? 0,
        prestation_confirmee: found?.prestation_confirmee ?? 0,
        location_non_confirmee: found?.location_non_confirmee ?? 0,
        prestation_non_confirmee: found?.prestation_non_confirmee ?? 0,
        total: found?.total ?? 0,
      };
    });
  }, [data, year]);

  // Totaux annuels (affichés, mais exclus de l'export copier)
  const yearTotals = useMemo(() => {
    return rowsForYear.reduce((acc, row) => ({
      location_confirmee: acc.location_confirmee + row.location_confirmee,
      prestation_confirmee: acc.prestation_confirmee + row.prestation_confirmee,
      location_non_confirmee: acc.location_non_confirmee + row.location_non_confirmee,
      prestation_non_confirmee: acc.prestation_non_confirmee + row.prestation_non_confirmee,
      total: acc.total + row.total,
    }), { location_confirmee: 0, prestation_confirmee: 0, location_non_confirmee: 0, prestation_non_confirmee: 0, total: 0 });
  }, [rowsForYear]);

  const fmt = (value) => value.toFixed(2).replace('.', ',');

  const handlePrevYear = useCallback(() => setYear(y => y - 1), []);
  const handleNextYear = useCallback(() => setYear(y => y + 1), []);

  // Gestion du raccourci clavier Échap pour fermer le popup
  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [open, onClose]);

  if (!open) return null;
  return (
    <div style={{
      position: "fixed", top: 0, left: 0, width: "100vw", height: "100vh", background: "rgba(0,0,0,0.3)", zIndex: 1000,
      display: "flex", alignItems: "center", justifyContent: "center"
    }}>
      <div style={{ background: "#fff", borderRadius: 8, padding: 24, minWidth: 350, maxWidth: 600, boxShadow: "0 2px 16px #0003", position: 'relative' }}>
        <button onClick={onClose} style={{ position: 'absolute', top: 8, right: 8, background: 'none', border: 'none', fontSize: 24, cursor: 'pointer', color: '#888', lineHeight: 1 }} aria-label="Fermer">
          ×
        </button>
        <div style={{display:'flex',alignItems:'center',justifyContent:'center',marginBottom:8}}>
          <button onClick={handlePrevYear} style={{fontSize:20,marginRight:12,background:'none',border:'none',cursor:'pointer',color:'#888'}} aria-label="Année précédente">←</button>
          <h2 style={{margin:0}}>{year}</h2>
          <button onClick={handleNextYear} style={{fontSize:20,marginLeft:12,background:'none',border:'none',cursor:'pointer',color:'#888'}} aria-label="Année suivante">→</button>
        </div>
        {loading && <div style={{textAlign:'center', color:'#888', marginBottom:8}}>Chargement...</div>}
        {error && <div style={{textAlign:'center', color:'red', marginBottom:8}}>{error}</div>}
        <button
          style={{
            marginBottom: 12,
            padding: '6px 18px',
            borderRadius: 4,
            border: copied ? '2px solid #4caf50' : '1px solid #bbb',
            background: copied ? '#e8f5e9' : '#f7f7f7',
            color: copied ? '#256029' : '#222',
            fontWeight: 500,
            cursor: 'pointer',
            transition: 'all 0.2s',
            outline: copied ? 'none' : undefined
          }}
          onClick={() => {
            // Génère le texte à copier (tabulé, compatible tableur) - sans la ligne des totaux
            const header1 = ['','Confirmés','','Non confirmés','','Total'];
            const header2 = ['Mois','Location','Prestation','Location','Prestation','Total'];
            const rows = rowsForYear.map((row, i) => [
              MONTHS[i],
              fmt(row.location_confirmee),
              fmt(row.prestation_confirmee),
              fmt(row.location_non_confirmee),
              fmt(row.prestation_non_confirmee),
              fmt(row.total)
            ].join('\t'));
            const text = [
              header1.join('\t'),
              header2.join('\t'),
              ...rows
            ].join('\n');
            navigator.clipboard.writeText(text);
            setCopied(true);
            setTimeout(() => setCopied(false), 1800);
          }}
          aria-label="Copier les statistiques dans le presse-papier"
        >
          {copied ? '✔ Table copiée !' : '📋 Copier les statistiques'}
        </button>
        <table style={{ width: "100%", marginTop: 8, borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <th></th>
              <th colSpan={2} style={{borderBottom:'1px solid #ccc'}}>Confirmés</th>
              <th colSpan={2} style={{borderBottom:'1px solid #ccc'}}>Non confirmés</th>
              <th rowSpan={2} style={{verticalAlign:'middle'}}>Total</th>
            </tr>
            <tr>
              <th>Mois</th>
              <th>Location</th>
              <th>Prestation</th>
              <th>Location</th>
              <th>Prestation</th>
            </tr>
          </thead>
          <tbody>
            {rowsForYear.map((row, i) => (
              <tr key={row.mois}>
                <td>{MONTHS[i]}</td>
                <td>{fmt(row.location_confirmee)}</td>
                <td>{fmt(row.prestation_confirmee)}</td>
                <td>{fmt(row.location_non_confirmee)}</td>
                <td>{fmt(row.prestation_non_confirmee)}</td>
                <td>{fmt(row.total)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td style={{borderTop:'2px solid #888', fontWeight:700, padding:'8px 4px'}}>Total</td>
              <td style={{borderTop:'2px solid #888', fontWeight:700, padding:'8px 4px', background:'#f5f5f5'}}>{fmt(yearTotals.location_confirmee)}</td>
              <td style={{borderTop:'2px solid #888', fontWeight:700, padding:'8px 4px', background:'#f5f5f5'}}>{fmt(yearTotals.prestation_confirmee)}</td>
              <td style={{borderTop:'2px solid #888', fontWeight:700, padding:'8px 4px', background:'#f5f5f5'}}>{fmt(yearTotals.location_non_confirmee)}</td>
              <td style={{borderTop:'2px solid #888', fontWeight:700, padding:'8px 4px', background:'#f5f5f5'}}>{fmt(yearTotals.prestation_non_confirmee)}</td>
              <td style={{borderTop:'2px solid #888', fontWeight:700, padding:'8px 4px', background:'#e8f5e9', color:'#256029'}}>{fmt(yearTotals.total)}</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}

